import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from "react";
import { useBusyAction } from "../lib/busy";
import { LightMotion } from "../components/LightMotion";

/**
 * A button that answers its press (see `useBusyAction`).
 *
 * While `onPress` is working -- or while `busy` says some work this press
 * started still is -- the label is replaced, in place, by `busyLabel` with the
 * shared painted light; the button keeps its width, takes no second press, and
 * stays focusable. `onPress` returns the work's promise. A rejection is the
 * caller's to show: it is swallowed here only so that it is not reported twice.
 */
export function BusyButton({ onPress, busyLabel, busy: outside = false, holdMs = 400, className = "btn", children, disabled, ...rest }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "children"> & {
  onPress: (event: MouseEvent<HTMLButtonElement>) => unknown;
  /** What the control says while it works: 正在保存… */
  busyLabel: string;
  /** Work from this press that goes on after `onPress` has returned. */
  busy?: boolean;
  holdMs?: number;
  children: ReactNode;
}) {
  const action = useBusyAction(holdMs);
  const working = action.busy || outside;
  return <button type="button" {...rest} className={className} disabled={disabled}
    data-state={working ? "working" : "waiting"}
    aria-busy={working || undefined} aria-disabled={working && !disabled ? true : undefined}
    onClick={event => { if (!outside) void action.run(() => onPress(event)).catch(() => {}); }}>
    <span className="btn-label">
      <span className={working ? "is-reserved" : undefined} aria-hidden={working || undefined}>{children}</span>
      <span className={working ? undefined : "is-reserved"} aria-hidden={working ? undefined : true}>
        <LightMotion compact state={working ? "working" : "waiting"} />{busyLabel}</span>
    </span>
  </button>;
}
